import React, { useCallback, useMemo } from "react";
import styled from "styled-components";
import isEmpty from 'ramda/es/isEmpty';
import { useHistory } from "react-router";
import { Grid } from "../../UI/Grid";
import { useEntrataIntegrationContext } from "./EntrataIntegrationProvider";
import { IIntegrationsEntrata } from "../../services/IntegrationApi";
import { Typography } from "../../UI/Typography";
import { getDateFromArray } from "../../helpers/getDateFromArray";
import { URL } from "../../constants/navigation";
import MUILink from "@mui/material/Link";
import { HelpTooltip } from "../HelpTooltip";
import { handleNavigation } from "../../helpers/handleNavigation";

const IMPORT_COMPLETE_HELP_TITLE = "Import Complete Units:";
const IMPORT_COMPLETE_HELP_CONTENT = "If a row of the Entrata BUA is completely filled for the unit type, we treat this is a full unit.";
const ENABLE_ACCESS_HELP_TITLE = "Enabled Access";
const ENABLE_ACCESS_HELP_CONTENT = "Residents will only be imported and sent an invitation when they meet import criteria when your matching period is active.";

const Link = styled(MUILink)`
  cursor: pointer;
  text-decoration: underline;
  display: inline-block;
  color: #007bff !important;
`;

export const IntegrationEntrataExtraInfo: React.FC = () => {
  const { push } = useHistory();
  const { integrations } = useEntrataIntegrationContext();

  const { propertyIds, lastImportRun } = useMemo(() => ({
    lastImportRun: isEmpty(integrations.lastImportRun) || integrations.lastImportRun === null ? '' : getDateFromArray(integrations.lastImportRun).format("MM/DD/YYYY"),
    propertyIds: new Set((integrations.mappings as IIntegrationsEntrata[]).map((x: IIntegrationsEntrata) => x.entrataPropertyId))
  }), [integrations.mappings]);

  const navToMatchRules = useCallback((e) => {
    e.preventDefault();
    handleNavigation(e, URL.MATCH_RULES, push);
  }, [push]);

  const navToNetwork = useCallback((e) => {
    e.preventDefault();
    handleNavigation(e, URL.NETWORK, push);
  }, [push]);

  return (
    <Grid container mb={3}>
      <Grid item xs={12} mb={2}>
        <Typography variant="subtitle1">
          The information below shows how your Entrata data is linked to your RoomSync floor plans as well as the lease criteria used to identify which residents to import.
        </Typography>
      </Grid>
      <Grid item xs={12} mb={2}>
        <Typography variant="subtitle1">
          When a resident reaches Approved status in Entrata with a lease that meets the criteria shown in the Date Criteria columns below, they'll be imported based on the below Entrata settings and added to the corresponding RoomSync floor plan. Residents will only be imported and sent an invitation when they meet import criteria during your <Link onClick={navToMatchRules} href={URL.MATCH_RULES}>Matching Rounds.</Link>
        </Typography>
      </Grid>
      <Grid item xs={12} mb={4}>
        <Typography variant="subtitle1">
          To view a list of your unit and room types along with the max number of residents allowed in a match, visit <Link onClick={navToNetwork}>Settings - Network.</Link>
        </Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="subtitle1">
          <b>Entrata ID:</b> {propertyIds}
        </Typography>
      </Grid>
      <Grid item xs={6} align='right'>
        <HelpTooltip title={ENABLE_ACCESS_HELP_TITLE} text={ENABLE_ACCESS_HELP_CONTENT} justifyContent='flex-end'>
          <Typography variant="subtitle1">
            <b>Import Enabled:</b> {integrations.importEnabled ? "Yes" : "No"}
          </Typography>
        </HelpTooltip>
      </Grid>
      <Grid item xs={6}>
        <HelpTooltip title={IMPORT_COMPLETE_HELP_TITLE} text={IMPORT_COMPLETE_HELP_CONTENT} justifyContent='flex-start'>
          <Typography variant="subtitle1">
            <b>Import Complete Units:</b> {integrations.importCompleteUnits ? "Yes" : "No"}
          </Typography>
        </HelpTooltip>
      </Grid>
      <Grid item xs={6} align='right'>
        <Typography variant="subtitle1">
          <b>Invite Complete Units:</b> {integrations.inviteCompleteUnits ? "Yes" : "No"}
        </Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="subtitle1">
          <b>Last Import Run:</b> {lastImportRun || '-'}
        </Typography>
      </Grid>
    </Grid>
  );
};
